import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
} from "@mui/material";
import {
  CalendarToday,
  Contacts,
  Download,
  ForumOutlined,
} from "@mui/icons-material";
import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import BackBtn from "../../widgets/BackBtn";
import useApiEp from "../../hooks/useApiEp";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

const DATE_FILTERS = [
  { value: "all", label: "All time" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last7days", label: "Last 7 days" },
  { value: "last30days", label: "Last 30 days" },
  { value: "custom", label: "Custom range" },
];

const getDateRange = (dateFilter, customStartDate, customEndDate) => {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  switch (dateFilter) {
    case "today":
      return { startDate: today.toISOString(), endDate: now.toISOString() };
    case "yesterday": {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return {
        startDate: yesterday.toISOString(),
        endDate: today.toISOString(),
      };
    }
    case "last7days": {
      const last7 = new Date(today);
      last7.setDate(last7.getDate() - 7);
      return { startDate: last7.toISOString(), endDate: now.toISOString() };
    }
    case "last30days": {
      const last30 = new Date(today);
      last30.setDate(last30.getDate() - 30);
      return { startDate: last30.toISOString(), endDate: now.toISOString() };
    }
    case "custom":
      if (!customStartDate || !customEndDate) return null;
      return {
        startDate: new Date(`${customStartDate}T00:00:00`).toISOString(),
        endDate: new Date(`${customEndDate}T23:59:59.999`).toISOString(),
      };
    default:
      return null;
  }
};

const appendDateRange = (params, dateRange) => {
  if (!dateRange) return;
  params.set("startDate", dateRange.startDate);
  params.set("endDate", dateRange.endDate);
};

const readResponseData = async (endpoint) => {
  const response = await fetch(`${API_ENDPOINT}${endpoint}`, getHeader());
  const payload = await response.json().catch(() => ({}));

  if (!response.ok) {
    throw new Error(payload.message || "Unable to export responses");
  }

  if (Array.isArray(payload)) return payload;
  return Array.isArray(payload.data) ? payload.data : [];
};

const flattenRecord = (item) => ({
  "Message ID": item.messageId,
  From: item.from,
  Message: item.body,
  "Reply Type": item.replyType,
  "Date & Time": new Date(item.createdOn).toLocaleString(),
  ...(item.name !== undefined ? { Name: item.name } : {}),
  ...(item.email !== undefined ? { Email: item.email } : {}),
  ...(item.phoneNumber !== undefined
    ? { "Phone Number": item.phoneNumber }
    : {}),
  ...Object.fromEntries(
    Object.entries(item).filter(
      ([key]) =>
        ![
          "messageId",
          "from",
          "body",
          "replyType",
          "createdOn",
          "_id",
          "to",
        ].includes(key)
    )
  ),
});

const writeResponsesWorkbook = (rows, sheetName, fileName, withContacts) => {
  const exportRows = withContacts
    ? rows.map(flattenRecord)
    : rows.map((item) => ({
        "Message ID": item.messageId,
        From: item.from,
        Message: item.body,
        "Reply Type": item.replyType,
        "Date & Time": new Date(item.createdOn).toLocaleString(),
      }));
  const worksheet = XLSX.utils.json_to_sheet(exportRows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  XLSX.writeFile(workbook, fileName);
};

const ContactsExportDialog = ({ open, onClose, exportEndpoint }) => {
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");
  const {
    data: groupList,
    isLoading: groupsLoading,
    isError: groupsError,
  } = useApiEp("/user/group-list", open);

  const handleClose = () => {
    if (exporting) return;
    setSelectedGroup(null);
    setExportError("");
    onClose();
  };

  const handleExport = async () => {
    if (!selectedGroup || exporting) return;
    setExporting(true);
    setExportError("");

    try {
      const separator = exportEndpoint.includes("?") ? "&" : "?";
      const endpoint = `${exportEndpoint}${separator}groupName=${encodeURIComponent(
        selectedGroup
      )}`;
      const rows = await readResponseData(endpoint);

      if (!rows.length) {
        setExportError("No matching responses were found for this contact group.");
        return;
      }

      writeResponsesWorkbook(
        rows,
        "Responses with Contacts",
        `RCS_Responses_${selectedGroup}_${
          new Date().toISOString().split("T")[0]
        }.xlsx`,
        true
      );
      setSelectedGroup(null);
      onClose();
    } catch (error) {
      setExportError(error.message || "Export failed. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 750, pb: 1 }}>
        Export with contact details
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2.5 }}>
          Select a contact group. The complete filtered response set will be
          matched by phone number and exported with available contact fields.
        </Typography>

        {groupsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Contact groups could not be loaded. Please try again.
          </Alert>
        )}
        {exportError && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {exportError}
          </Alert>
        )}

        <Autocomplete
          options={Array.isArray(groupList) ? groupList : []}
          loading={groupsLoading}
          value={selectedGroup}
          onChange={(_, value) => {
            setSelectedGroup(value);
            setExportError("");
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Contact group"
              placeholder="Search or select a group"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {groupsLoading && (
                      <CircularProgress color="inherit" size={16} />
                    )}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          noOptionsText={groupsLoading ? "Loading groups…" : "No groups found"}
          isOptionEqualToValue={(option, value) => option === value}
          fullWidth
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={handleClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={
            exporting ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <Download />
            )
          }
          onClick={handleExport}
          disabled={!selectedGroup || exporting || groupsError}
        >
          {exporting ? "Preparing export…" : "Export Excel"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const UserResponses = () => {
  const [dateFilter, setDateFilter] = useState("all");
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [contactsDialogOpen, setContactsDialogOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState("");

  const customRangeReady =
    dateFilter !== "custom" || Boolean(customStartDate && customEndDate);
  const dateRange = useMemo(
    () => getDateRange(dateFilter, customStartDate, customEndDate),
    [dateFilter, customStartDate, customEndDate]
  );

  const apiEndpoint = useMemo(() => {
    const params = new URLSearchParams({
      page: String(page + 1),
      limit: String(rowsPerPage),
    });
    appendDateRange(params, dateRange);
    return `/rcs/user-responses?${params.toString()}`;
  }, [dateRange, page, rowsPerPage]);

  const exportEndpoint = useMemo(() => {
    const params = new URLSearchParams();
    appendDateRange(params, dateRange);
    const query = params.toString();
    return `/rcs/user-responses/export${query ? `?${query}` : ""}`;
  }, [dateRange]);

  const { data, isLoading, isError, error } = useApiEp(
    apiEndpoint,
    customRangeReady,
    0
  );
  const responses = Array.isArray(data) ? data : data?.data || [];
  const totalItems = Number(data?.totalItems ?? responses.length) || 0;
  const responseErrorMessage =
    error?.response?.data?.message ||
    error?.message ||
    "Please try again.";
  const fromRow = totalItems === 0 ? 0 : page * rowsPerPage + 1;
  const toRow = Math.min((page + 1) * rowsPerPage, totalItems);

  const getReplyTypeColor = (type) => {
    switch (type?.toLowerCase()) {
      case "positive":
        return "success";
      case "negative":
        return "error";
      case "button":
        return "secondary";
      case "neutral":
        return "default";
      default:
        return "primary";
    }
  };

  const handleDateFilterChange = (_, value) => {
    if (value === null) return;
    setDateFilter(value);
    setPage(0);
    setExportError("");
  };

  const handlePlainExport = async () => {
    if (exporting || totalItems === 0) return;
    setExporting(true);
    setExportError("");

    try {
      const rows = await readResponseData(exportEndpoint);
      if (!rows.length) {
        setExportError("No responses are available for the selected period.");
        return;
      }
      writeResponsesWorkbook(
        rows,
        "User Responses",
        `RCS_User_Responses_${new Date().toISOString().split("T")[0]}.xlsx`,
        false
      );
    } catch (exportRequestError) {
      setExportError(
        exportRequestError.message || "Export failed. Please try again."
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <BackBtn />

      <Paper
        elevation={0}
        sx={{
          mt: 2,
          mb: 3,
          p: { xs: 2.25, md: 3 },
          borderRadius: 3,
          color: "#132238",
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(239,246,255,0.98) 0%, rgba(250,245,255,0.96) 55%, #fff 100%)",
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "stretch", md: "center" }}
          spacing={2.5}
        >
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              sx={{
                width: 52,
                height: 52,
                bgcolor: "primary.main",
                boxShadow: (theme) =>
                  `0 10px 24px ${alpha(theme.palette.primary.main, 0.24)}`,
              }}
            >
              <ForumOutlined />
            </Avatar>
            <Box>
              <Typography variant="h4" fontWeight={800}>
                RCS campaign responses
              </Typography>
              <Typography color="text.secondary" sx={{ mt: 0.35 }}>
                Review customer replies and export response data for follow-up.
              </Typography>
            </Box>
          </Stack>

          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <Button
              variant="outlined"
              startIcon={
                exporting ? <CircularProgress size={16} /> : <Download />
              }
              onClick={handlePlainExport}
              disabled={
                !totalItems || exporting || !customRangeReady || isLoading || isError
              }
            >
              {exporting ? "Preparing…" : "Export responses"}
            </Button>
            <Button
              variant="contained"
              startIcon={<Contacts />}
              onClick={() => setContactsDialogOpen(true)}
              disabled={
                !totalItems || exporting || !customRangeReady || isLoading || isError
              }
            >
              Export with contacts
            </Button>
          </Stack>
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: { xs: 2, md: 2.5 },
          mb: 2.5,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
        }}
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Box>
              <Stack direction="row" spacing={1} alignItems="center">
                <CalendarToday color="primary" sx={{ fontSize: 19 }} />
                <Typography fontWeight={750}>Response period</Typography>
              </Stack>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.4 }}
              >
                Exports include all responses matching this filter, not only the
                current page.
              </Typography>
            </Box>
            {customRangeReady && !isLoading && !isError && (
              <Chip
                icon={<ForumOutlined />}
                label={`${totalItems.toLocaleString()} total response${
                  totalItems === 1 ? "" : "s"
                }`}
                color="primary"
                variant="outlined"
                sx={{
                  alignSelf: { xs: "flex-start", sm: "center" },
                  fontWeight: 700,
                }}
              />
            )}
          </Stack>

          <Box sx={{ overflowX: "auto", pb: 0.25 }}>
            <ToggleButtonGroup
              value={dateFilter}
              exclusive
              onChange={handleDateFilterChange}
              size="small"
              sx={{ minWidth: "max-content" }}
            >
              {DATE_FILTERS.map((filter) => (
                <ToggleButton
                  key={filter.value}
                  value={filter.value}
                  sx={{ px: { xs: 1.4, sm: 2 }, textTransform: "none" }}
                >
                  {filter.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>

          {dateFilter === "custom" && (
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              <TextField
                label="Start date"
                type="date"
                value={customStartDate}
                onChange={(event) => {
                  setCustomStartDate(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="End date"
                type="date"
                value={customEndDate}
                onChange={(event) => {
                  setCustomEndDate(event.target.value);
                  setPage(0);
                }}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: customStartDate }}
                size="small"
              />
            </Stack>
          )}
        </Stack>
      </Paper>

      {exportError && (
        <Alert
          severity="warning"
          onClose={() => setExportError("")}
          sx={{ mb: 2 }}
        >
          {exportError}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          overflow: "hidden",
        }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          spacing={0.5}
          sx={{ px: 2.5, py: 2 }}
        >
          <Typography variant="h6" fontWeight={750}>
            Response activity
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {!customRangeReady
              ? "Select a custom date range"
              : isLoading
              ? "Loading responses…"
              : isError
              ? "Unable to load responses"
              : totalItems > 0
              ? `Showing ${fromRow.toLocaleString()}–${toRow.toLocaleString()} of ${totalItems.toLocaleString()}`
              : "No responses in this period"}
          </Typography>
        </Stack>

        {customRangeReady && isLoading && <LinearProgress />}

        {!customRangeReady ? (
          <Box sx={{ py: 6, px: 2, textAlign: "center" }}>
            <Typography color="text.secondary">
              Select both dates to view responses for a custom period.
            </Typography>
          </Box>
        ) : isError ? (
          <Alert severity="error" sx={{ mx: 2.5, mb: 2.5 }}>
            Responses could not be loaded. {responseErrorMessage}
          </Alert>
        ) : isLoading ? null : !responses.length ? (
          <Box sx={{ py: 8, px: 2, textAlign: "center" }}>
            <Avatar
              sx={{
                mx: "auto",
                mb: 1.5,
                bgcolor: "action.hover",
                color: "text.secondary",
              }}
            >
              <ForumOutlined />
            </Avatar>
            <Typography fontWeight={700}>No responses found</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Try another date range when new customer replies arrive.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table aria-label="RCS user responses">
              <TableHead>
                <TableRow>
                  {[
                    "Message ID",
                    "Customer",
                    "Response",
                    "Type",
                    "Received",
                  ].map((heading) => (
                    <TableCell
                      key={heading}
                      sx={{
                        bgcolor: "#f8fafc",
                        color: "text.secondary",
                        fontWeight: 750,
                        borderTop: "1px solid",
                        borderColor: "divider",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {heading}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {responses.map((response, index) => {
                  const receivedAt = new Date(response.createdOn);
                  const validDate = !Number.isNaN(receivedAt.getTime());
                  return (
                    <TableRow
                      hover
                      key={response.messageId || response._id || index}
                      sx={{ "&:last-child td": { borderBottom: 0 } }}
                    >
                      <TableCell sx={{ maxWidth: 190 }}>
                        <Tooltip title={response.messageId || "Not available"}>
                          <Typography
                            variant="body2"
                            fontFamily="monospace"
                            noWrap
                            color="text.secondary"
                          >
                            {response.messageId || "—"}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Typography fontWeight={650} whiteSpace="nowrap">
                          {response.from || "Unknown"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260, maxWidth: 480 }}>
                        <Typography
                          sx={{
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {response.body || "—"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={response.replyType || "Text reply"}
                          color={getReplyTypeColor(response.replyType)}
                          size="small"
                          variant="outlined"
                          sx={{ fontWeight: 650, whiteSpace: "nowrap" }}
                        />
                      </TableCell>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>
                        {validDate ? (
                          <>
                            <Typography variant="body2" fontWeight={650}>
                              {receivedAt.toLocaleDateString()}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {receivedAt.toLocaleTimeString([], {
                                hour: "2-digit",
                                minute: "2-digit",
                              })}
                            </Typography>
                          </>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {customRangeReady && !isLoading && !isError && totalItems > 0 && (
          <TablePagination
            component="div"
            count={totalItems}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page"
            labelDisplayedRows={({ from, to, count }) =>
              `${from.toLocaleString()}–${to.toLocaleString()} of ${count.toLocaleString()}`
            }
          />
        )}
      </Paper>

      <ContactsExportDialog
        open={contactsDialogOpen}
        onClose={() => setContactsDialogOpen(false)}
        exportEndpoint={exportEndpoint}
      />
    </Container>
  );
};

export default UserResponses;
